// OrderBump.tsx
import React, { useEffect, useRef } from "react";
import useTheme from "../hooks/useTheme";
import { useThemeContext } from "./ThemeContext";
import { ArrowRightIcon } from "@heroicons/react/20/solid";
import { PortableText, PortableTextComponents } from "@portabletext/react";

interface BumpProduct {
  name?: string;
  sku: string;
  price: number;
  image?: string;
  imageUrl?: string;
  headline?: string;
  description?: string;
  extendedDescription?: any;
  display?: boolean;
  hidden?: boolean;
  compareAt?: string;
}

interface OrderBumpProps {
  bumpProducts: BumpProduct[];
  currency: string;
  onBumpChange: (index: number, addBump: boolean) => void;
}

const OrderBump: React.FC<OrderBumpProps> = ({
  bumpProducts,
  currency,
  onBumpChange,
}) => {
  const { theme } = useThemeContext();
  const { primaryBg, secondaryBg, accent1, border } = useTheme(theme); // Use theme hook

  const hasEffectRun = useRef(false);

  useEffect(() => {
    if (!hasEffectRun.current) {
      bumpProducts.forEach((bumpProduct, index) => {
        if (bumpProduct.hidden) {
          onBumpChange(index, true);
        }
      });
      hasEffectRun.current = true;
    }
  }, [bumpProducts, onBumpChange]);
  const components: PortableTextComponents = {
    list: {
      bullet: ({ children }: any) => (
        <ul className="mb-4 list-disc space-y-1 text-black">{children}</ul>
      ),
      number: ({ children }: any) => (
        <ol className="mb-4 list-decimal	 space-y-1 text-black">{children}</ol>
      ),
      check: ({ children }: any) => <ul className="pl-0">{children}</ul>,
    },
    listItem: {
      // Ex. 2: rendering custom list items
      bullet: ({ children }: any) => <li className="text-black">{children}</li>,
      number: ({ children }: any) => <li className="text-black">{children}</li>,
      check: ({ children }: any) => (
        <li className="flex gap-4">
          <span className="mt-0.5 flex-none text-contrast">
            <svg
              width="26"
              height="26"
              viewBox="0 0 26 26"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="fill-current"
            >
              <path d="M10.8328 16.4355L20.7912 6.47705L22.3232 8.00911L10.8328 19.4996L3.93848 12.6053L5.47055 11.0733L10.8328 16.4355Z" />
            </svg>
          </span>
          <span className="text-black">{children}</span>
        </li>
      ),
    },

    block: {
      h1: ({ children }: any) => <h1>{children}</h1>,
      h2: ({ children }: any) => <h2>{children}</h2>,
      h2Large: ({ children }: any) => <h2 className="h2-large">{children}</h2>,
      h3: ({ children }: any) => <h3>{children}</h3>,
      h4: ({ children }: any) => <h4>{children}</h4>,
      h5: ({ children }: any) => <h5>{children}</h5>,
      h6: ({ children }: any) => <h6>{children}</h6>,
      normal: ({ children }: any) => <p>{children}</p>,
      small: ({ children }: any) => <p className="small">{children}</p>,
      large: ({ children }: any) => (
        <p className="not-prose mb-5 text-lg md:mb-10">{children}</p>
      ),
      label: ({ children }: any) => (
        <div className="mb-4 inline-flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-contrast"></span>
          <span className="font-mono text-xs uppercase">{children}</span>
        </div>
      ),
      blockquote: ({ children }: any) => <blockquote>{children}</blockquote>,
    },
  };

  return (
    <div>
      {bumpProducts.map((bumpProduct, index) => (
        <div
          key={index}
          className={`mt-4 ${bumpProduct.hidden ? "hidden" : ""}`}
        >
          <div
            className={`${border} divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow mb-4 `}
          >
            <div
              className={`${primaryBg} px-4 py-4 sm:px-6 flex items-center `}
            >
              <ArrowRightIcon
                aria-hidden="true"
                className={`${accent1} w-7 h-7 mr-2`}
              />

              <input
                type="checkbox"
                className={`h-4 w-4 rounded border-gray-300 text-black focus:ring-gray-600 mr-2`}
                onChange={(e) => onBumpChange(index, e.target.checked)}
              />
              <span className="font-bold">
                {bumpProduct.headline ? (
                  <span
                    dangerouslySetInnerHTML={{ __html: bumpProduct.headline }}
                  />
                ) : (
                  bumpProduct.name
                )}
              </span>
            </div>
            <div className={` ${secondaryBg}  p-4`}>
              <div className="flex flex-col">
                <div className="">
                  {bumpProduct.extendedDescription ? (
                    <PortableText
                      value={bumpProduct.extendedDescription}
                      components={components}
                    />
                  ) : (
                    <p className="mt-1">{bumpProduct.description}</p>
                  )}
                </div>
                {bumpProduct.imageUrl ? (
                  <div className="mx-4 md:mt-0 flex items-center justify-center w-full">
                    <img
                      src={bumpProduct.imageUrl}
                      alt={bumpProduct.name}
                      width="300px"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default OrderBump;
